import { FormNumberInput } from "@/components/atom/numberInput/form/FormNumberInput";
import { FormTextInput } from "@/components/atom/textInput/form/FormTextInput";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import { BarcodeInput } from "@/components/molecule/barcodeInput/BarcodeInput";
import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import { InventoriesAutoComplete } from "@/components/organism/autoComplete/inventory/inventories-autoComplete";
import { LocationAutoComplete } from "@/components/organism/autoComplete/location/location-autoComplate";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInventoriesFormFetch } from "@/fetch/form/useInventoriesFormFetch";
import { useRawMaterialHandler } from "@/hooks/handler/useRawMaterialHandler";
import { useWorkDataStore } from "@/store/work.store";
import { customNotification } from "@/utils/notificationShow";
import { Flex, Pagination } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MRT_ColumnDef } from "mantine-react-table";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

const RawMaterial = Object.assign(
  {},
  {
    Template: FormTemplate,
    SubTitle: FormSubTitle,
    TextInput: FormTextInput,
    NumberInput: FormNumberInput,
    BarcodeInput: BarcodeInput,
    Table: MantineSelectableTable,
    InventoriesAutoComplete: InventoriesAutoComplete,
    LocationAutoComplete: LocationAutoComplete,
    Pagination: Pagination,
  }
);

export const RawMaterialForm = () => {
  const { closeModal } = useModal();
  const { onSubmit } = useRawMaterialHandler();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { work } = workData;
  const { t } = useTranslation();
  const bomItemCodes = work?.routingData?.routingBoms?.map(
    (value) => value.itemCode
  );

  const targetLocationCode = work?.locationSetting?.toLocation?.code;

  const routingBoms = work?.routingData?.routingBoms;
  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemName: bom.itemWithGrade?.name,
    gradeName: bom.itemWithGrade?.gradeName,
    itemCategoryName: bom.itemWithGrade?.itemCategoryName,
    abstractItemCode: bom.itemWithGrade?.abstractItemCode,
    itemCode: bom.itemCode,
    quantityNumerator: bom.quantityNumerator,
  }));

  const form = useForm({
    initialValues: {
      quantityNumerator: 0,
      predictTotalQuantity: 0,
      predictQuantity: 0,
      useBarcode: false,
      barcodeInput: "",
      itemCode: "",
      workId: work?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: "",
        targetLocationCode: targetLocationCode,
        quantity: 0,
      },
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: form.values.itemCode ?? bomItemCodes,
    locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
    lotId: form.values.barcodeInput,
  });

  const columns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "gradeName",
        header: COMMON_TEXT.COLUMN.GRADE,
      },
      {
        accessorKey: "itemCategoryName",
        header: COMMON_TEXT.COLUMN.COLOR,
      },
      {
        accessorKey: "abstractItemCode",
        header: COMMON_TEXT.COLUMN.SHAPE,
      },
      {
        accessorKey: "quantityNumerator",
        header: COMMON_TEXT.COLUMN.QUANTITY_NUMERATOR + "(%)",
      },
    ],
    []
  );

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data.data.data;
    const noData = !inventoryData?.length;
    if (noData) {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", "");
      form.setFieldValue("worksWorkIdInputPutRequest.quantity", 0);
      customNotification.error({
        message: COMMON_TEXT.NOTIFICATION.LOT_NOT_FOUND,
      });
    } else {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue(
        "worksWorkIdInputPutRequest.lotId",
        form.values.barcodeInput
      );
      form.setFieldValue(
        "worksWorkIdInputPutRequest.quantity",
        Number(inventoryData[0].quantity)
      );
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      form.setFieldValue("barcodeInput", form.values.barcodeInput);
      form.setFieldValue("useBarcode", true);
      refetchHandler();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    form.setFieldValue("barcodeInput", inputValue);
    form.setFieldValue("useBarcode", true);
    refetchHandler();
  };

  const handleOnSubmit = () => {
    const worksWorkIdInputPutRequest = {
      lotId: Number(form.values.worksWorkIdInputPutRequest.lotId),
      sourceLocationCode:
        form.values.worksWorkIdInputPutRequest.sourceLocationCode,
      targetLocationCode:
        form.values.worksWorkIdInputPutRequest.targetLocationCode,
      quantity: String(form.values.worksWorkIdInputPutRequest.quantity),
    };

    onSubmit({ worksWorkIdInputPutRequest: worksWorkIdInputPutRequest });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
    form.setFieldValue("quantityNumerator", selectedRows[0]?.quantityNumerator);
  };

  useEffect(() => {
    const itemCode = form.values.itemCode;
    const quantityNumerator = form.values.quantityNumerator;
    const predictTotalQuantity = form.values.predictTotalQuantity;

    if (itemCode && quantityNumerator && predictTotalQuantity) {
      const predictQuantity =
        Number(quantityNumerator) *
        Number(form.values.predictTotalQuantity) *
        0.01;
      form.setFieldValue("predictQuantity", predictQuantity);
    }
  }, [form.values.itemCode, form.values.predictTotalQuantity]);

  return (
    <RawMaterial.Template
      onSubmit={handleOnSubmit}
      onCancel={() => closeModal({})}
      isButtonDisabled={false}
    >
      <RawMaterial.Table
        disabledCheckBox={!form.values.predictTotalQuantity}
        enableMultiRowSelection={false}
        columns={columns}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />
      <RawMaterial.LocationAutoComplete
        withAsterisk
        label={COMMON_TEXT.LABEL.LOCATION}
        {...form.getInputProps("worksWorkIdInputPutRequest.sourceLocationCode")}
      />
      <RawMaterial.BarcodeInput
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        handleBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...form.getInputProps("barcodeInput")}
      />
      <RawMaterial.InventoriesAutoComplete
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        withAsterisk
        label={COMMON_TEXT.LABEL.LOT_INFORM}
        value={form.values.worksWorkIdInputPutRequest.lotId}
        query={
          form.values.itemCode
            ? {
                itemCode: form.values.itemCode,
                locationCode:
                  form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
            : {
                itemCode: bomItemCodes,
                locationCode:
                  form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
        }
        onChange={(value) => {
          const quantity = Number(
            data?.data?.filter((row) => row.lotId === Number(value))[0]
              ?.quantity ?? 0
          );
          form.setFieldValue("useBarcode", false);
          form.setFieldValue("worksWorkIdInputPutRequest.quantity", quantity);
          form.setFieldValue("worksWorkIdInputPutRequest.lotId", value ?? "");
        }}
      />
      <RawMaterial.TextInput
        withAsterisk
        disabled
        label={COMMON_TEXT.LABEL.TARGET_LOCATION_CODE}
        value={targetLocationCode}
      />

      <RawMaterial.NumberInput
        withAsterisk
        label={COMMON_TEXT.COLUMN.PREDICT_TOTAL_QUANTITY}
        {...form.getInputProps("predictTotalQuantity")}
      />
      <Flex justify={"space-between"}>
        <RawMaterial.NumberInput
          disabled
          label={COMMON_TEXT.COLUMN.REQUIRED_QUANTITY}
          value={form.values.predictQuantity}
        />
        <RawMaterial.NumberInput
          withAsterisk
          label={COMMON_TEXT.COLUMN.INPUT_QUANTITY}
          {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
        />
      </Flex>
    </RawMaterial.Template>
  );
};
